class RRRProductGallery {
  constructor(gallery) {
    this.gallery = gallery;
    this.viewer = gallery.querySelector('[data-rrr-gallery-viewer]');
    this.track = gallery.querySelector('[data-rrr-gallery-track]');
    this.slides = Array.from(gallery.querySelectorAll('[data-rrr-gallery-slide]'));
    this.thumbnails = Array.from(gallery.querySelectorAll('[data-rrr-thumbnail]'));
    this.paginationDots = Array.from(gallery.querySelectorAll('[data-rrr-pagination-dot]'));

    this.previousButton = gallery.querySelector('[data-rrr-gallery-previous]');
    this.nextButton = gallery.querySelector('[data-rrr-gallery-next]');
    this.counterCurrent = gallery.querySelector('[data-rrr-current-media]');

    this.lightbox = gallery.querySelector('[data-rrr-product-lightbox]');
    this.lightboxImage = gallery.querySelector('[data-rrr-lightbox-image]');
    this.lightboxClose = gallery.querySelector('[data-rrr-lightbox-close]');
    this.lightboxPrevious = gallery.querySelector('[data-rrr-lightbox-previous]');
    this.lightboxNext = gallery.querySelector('[data-rrr-lightbox-next]');
    this.lightboxContent = gallery.querySelector('[data-rrr-lightbox-content]');

    this.sectionId = gallery.dataset.sectionId || '';
    this.zoomEnabled = gallery.dataset.zoomEnabled === 'true';
    this.lightboxEnabled = gallery.dataset.lightboxEnabled === 'true';
    this.mobileQuery = window.matchMedia('(max-width: 749px)');

    this.currentIndex = this.getInitialIndex();
    this.lightboxIndex = this.currentIndex;
    this.scrollTimer = null;
    this.wheelLocked = false;
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.preloadedSources = new Set();
    this.zoomStates = new WeakMap();
    this.variantMediaMap = this.readVariantMediaMap();

    if (!this.viewer || !this.slides.length) return;

    this.setupImageLoadingStates();
    this.bindEvents();
    this.syncGallery(this.currentIndex, { scrollMobile: false, focusThumbnail: false });
    this.preloadAround(this.currentIndex);
  }

  getInitialIndex() {
    const activeIndex = this.slides.findIndex((slide) => slide.classList.contains('is-active'));
    return activeIndex >= 0 ? activeIndex : 0;
  }

  readVariantMediaMap() {
    const script = this.gallery.querySelector('[data-rrr-variant-media-map]');
    if (!script) return {};

    try {
      return JSON.parse(script.textContent || '{}');
    } catch (error) {
      console.warn('RRR gallery: variant media map could not be parsed.', error);
      return {};
    }
  }

  bindEvents() {
    this.thumbnails.forEach((thumbnail) => {
      thumbnail.addEventListener('click', () => {
        this.goTo(Number(thumbnail.dataset.mediaIndex), { source: 'thumbnail' });
      });
    });

    this.paginationDots.forEach((dot) => {
      dot.addEventListener('click', () => {
        this.goTo(Number(dot.dataset.mediaIndex), { source: 'pagination' });
      });
    });

    this.previousButton?.addEventListener('click', () => {
      this.goTo(this.getPreviousIndex(this.currentIndex), { source: 'arrow' });
    });

    this.nextButton?.addEventListener('click', () => {
      this.goTo(this.getNextIndex(this.currentIndex), { source: 'arrow' });
    });

    this.viewer.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.goTo(this.getPreviousIndex(this.currentIndex), { source: 'keyboard' });
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.goTo(this.getNextIndex(this.currentIndex), { source: 'keyboard' });
      }

      if (event.key === 'Enter' && this.lightboxEnabled) {
        event.preventDefault();
        this.openLightbox(this.currentIndex);
      }
    });

    this.viewer.addEventListener(
      'scroll',
      () => {
        if (!this.mobileQuery.matches) return;

        window.clearTimeout(this.scrollTimer);
        this.scrollTimer = window.setTimeout(() => this.handleMobileScrollEnd(), 80);
      },
      { passive: true }
    );

    this.gallery.querySelectorAll('[data-rrr-image-button]').forEach((button) => {
      button.addEventListener('pointerenter', (event) => this.startZoom(event, button));
      button.addEventListener('pointermove', (event) => this.moveZoom(event, button));
      button.addEventListener('pointerleave', () => this.stopZoom(button));

      button.addEventListener('click', () => {
        const slide = button.closest('[data-rrr-gallery-slide]');
        const index = this.slides.indexOf(slide);

        if (this.lightboxEnabled) {
          this.openLightbox(index);
        }
      });
    });

    this.lightboxClose?.addEventListener('click', () => this.closeLightbox());
    this.lightboxPrevious?.addEventListener('click', () => this.showPreviousLightboxImage());
    this.lightboxNext?.addEventListener('click', () => this.showNextLightboxImage());

    this.lightbox?.addEventListener('click', (event) => {
      if (event.target === this.lightbox || event.target === this.lightboxContent) {
        this.closeLightbox();
      }
    });

    this.lightbox?.addEventListener(
      'wheel',
      (event) => {
        if (!this.lightbox.classList.contains('is-open') || this.wheelLocked) return;
        if (Math.abs(event.deltaY) < 18 && Math.abs(event.deltaX) < 18) return;

        event.preventDefault();
        this.wheelLocked = true;

        if (event.deltaY > 0 || event.deltaX > 0) {
          this.showNextLightboxImage();
        } else {
          this.showPreviousLightboxImage();
        }

        window.setTimeout(() => {
          this.wheelLocked = false;
        }, 420);
      },
      { passive: false }
    );

    this.lightbox?.addEventListener(
      'touchstart',
      (event) => {
        const touch = event.changedTouches[0];
        this.touchStartX = touch.clientX;
        this.touchStartY = touch.clientY;
      },
      { passive: true }
    );

    this.lightbox?.addEventListener(
      'touchend',
      (event) => {
        const touch = event.changedTouches[0];
        const deltaX = touch.clientX - this.touchStartX;
        const deltaY = touch.clientY - this.touchStartY;

        if (Math.abs(deltaX) < 55 || Math.abs(deltaX) <= Math.abs(deltaY)) return;

        if (deltaX < 0) {
          this.showNextLightboxImage();
        } else {
          this.showPreviousLightboxImage();
        }
      },
      { passive: true }
    );

    document.addEventListener('keydown', (event) => {
      if (!this.lightbox?.classList.contains('is-open')) return;

      if (event.key === 'Escape') this.closeLightbox();
      if (event.key === 'ArrowLeft') this.showPreviousLightboxImage();
      if (event.key === 'ArrowRight') this.showNextLightboxImage();
    });

    ['variant:change', 'product:variant-change', 'rrr:variant-merger:change'].forEach((eventName) => {
      document.addEventListener(eventName, (event) => this.handleVariantEvent(event));
    });

    document.addEventListener('rrr:gallery:select-media', (event) => {
      const detail = event.detail || {};
      if (detail.sectionId && String(detail.sectionId) !== String(this.sectionId)) return;
      if (detail.mediaId) this.selectMediaById(detail.mediaId, { source: 'external' });
    });

    const productScope = this.gallery.closest('product-info, product-component, .shopify-section') || document;
    productScope.addEventListener('change', () => {
      [0, 120, 350].forEach((delay) => {
        window.setTimeout(() => this.selectCurrentFormVariant(), delay);
      });
    });

    const handleBreakpointChange = () => {
      if (this.mobileQuery.matches) {
        this.scrollMobileGallery(this.currentIndex, 'auto');
      } else {
        this.updateDesktopGallery(this.currentIndex, false);
      }
    };

    if (typeof this.mobileQuery.addEventListener === 'function') {
      this.mobileQuery.addEventListener('change', handleBreakpointChange);
    } else {
      this.mobileQuery.addListener(handleBreakpointChange);
    }
  }

  setupImageLoadingStates() {
    this.slides.forEach((slide) => {
      const image = slide.querySelector('.rrr-product-gallery__main-image');
      if (!image) return;

      const markLoaded = () => {
        slide.classList.remove('is-loading');
        slide.classList.add('is-loaded');
      };

      if (image.complete && image.naturalWidth > 0) {
        markLoaded();
      } else {
        slide.classList.add('is-loading');
        image.addEventListener('load', markLoaded, { once: true });
        image.addEventListener('error', markLoaded, { once: true });
      }
    });
  }

  handleMobileScrollEnd() {
    const width = this.viewer.clientWidth;
    if (!width) return;

    const index = this.clampIndex(Math.round(this.viewer.scrollLeft / width));
    if (index === this.currentIndex) return;

    this.currentIndex = index;
    this.updateMobileState(index);
    this.preloadAround(index);
    this.dispatchGalleryChange('swipe');
  }

  handleVariantEvent(event) {
    const detail = event.detail || {};
    const eventSectionId = detail.sectionId || detail.section?.id;

    if (eventSectionId && String(eventSectionId) !== String(this.sectionId)) return;

    const variant = detail.variant || detail.productVariant || detail.selectedVariant;
    const mediaId =
      detail.mediaId ||
      variant?.featured_media?.id ||
      variant?.featuredMedia?.id ||
      this.variantMediaMap[String(variant?.id || detail.variantId || '')];

    if (mediaId) this.selectMediaById(mediaId, { source: 'variant' });
  }

  selectCurrentFormVariant() {
    const productScope = this.gallery.closest('product-info, product-component, .shopify-section') || document;
    const variantInput = productScope.querySelector(
      'product-form input[name="id"], form[action*="/cart/add"] input[name="id"], select[name="id"]'
    );

    if (!variantInput?.value) return;

    const mediaId = this.variantMediaMap[String(variantInput.value)];
    if (mediaId) this.selectMediaById(mediaId, { source: 'variant-form' });
  }

  goTo(index, options = {}) {
    const safeIndex = this.clampIndex(index);
    this.currentIndex = safeIndex;

    this.syncGallery(safeIndex, {
      scrollMobile: true,
      focusThumbnail: options.source === 'thumbnail',
      mobileBehavior: options.behavior || 'smooth'
    });

    this.preloadAround(safeIndex);
    this.dispatchGalleryChange(options.source || 'api');
  }

  syncGallery(index, options = {}) {
    if (this.mobileQuery.matches) {
      if (options.scrollMobile !== false) {
        this.scrollMobileGallery(index, options.mobileBehavior || 'smooth');
      }
      this.updateMobileState(index);
    } else {
      this.updateDesktopGallery(index, options.focusThumbnail === true);
    }
  }

  updateDesktopGallery(index, focusThumbnail = false) {
    this.slides.forEach((slide, slideIndex) => {
      const active = slideIndex === index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      if (!active) this.pauseMedia(slide);
    });

    this.updateControls(index);

    const activeThumbnail = this.thumbnails[index];
    if (activeThumbnail) {
      activeThumbnail.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      if (focusThumbnail) activeThumbnail.focus({ preventScroll: true });
    }
  }

  updateMobileState(index) {
    this.slides.forEach((slide, slideIndex) => {
      const active = slideIndex === index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      if (!active) this.pauseMedia(slide);
    });

    this.updateControls(index);
  }

  updateControls(index) {
    this.thumbnails.forEach((thumbnail, thumbnailIndex) => {
      const active = thumbnailIndex === index;
      thumbnail.classList.toggle('is-active', active);
      thumbnail.setAttribute('aria-current', active ? 'true' : 'false');
    });

    this.paginationDots.forEach((dot, dotIndex) => {
      dot.classList.toggle('is-active', dotIndex === index);
      dot.setAttribute('aria-current', dotIndex === index ? 'true' : 'false');
    });

    if (this.counterCurrent) this.counterCurrent.textContent = String(index + 1);
  }

  scrollMobileGallery(index, behavior = 'smooth') {
    this.viewer.scrollTo({ left: index * this.viewer.clientWidth, behavior });
  }

  selectMediaById(mediaId, options = {}) {
    const index = this.slides.findIndex(
      (slide) => String(slide.dataset.mediaId) === String(mediaId)
    );

    if (index < 0 || index === this.currentIndex) return;
    this.goTo(index, options);
  }

  startZoom(event, button) {
    if (!this.zoomEnabled || this.mobileQuery.matches || event.pointerType === 'touch') return;

    const image = button.querySelector('.rrr-product-gallery__main-image');
    if (!image) return;

    const state = {
      button,
      image,
      currentX: 50,
      currentY: 50,
      targetX: 50,
      targetY: 50,
      active: true,
      frame: null
    };

    this.zoomStates.set(button, state);
    button.classList.add('is-zoomed');
    this.moveZoom(event, button);
    this.runZoomFrame(state);
  }

  moveZoom(event, button) {
    const state = this.zoomStates.get(button);
    if (!state?.active) return;

    const bounds = button.getBoundingClientRect();
    state.targetX = Math.max(0, Math.min(100, ((event.clientX - bounds.left) / bounds.width) * 100));
    state.targetY = Math.max(0, Math.min(100, ((event.clientY - bounds.top) / bounds.height) * 100));
  }

  runZoomFrame(state) {
    if (!state.active) return;

    state.currentX += (state.targetX - state.currentX) * 0.16;
    state.currentY += (state.targetY - state.currentY) * 0.16;
    state.image.style.transformOrigin = `${state.currentX}% ${state.currentY}%`;

    state.frame = window.requestAnimationFrame(() => this.runZoomFrame(state));
  }

  stopZoom(button) {
    const state = this.zoomStates.get(button);
    if (state) {
      state.active = false;
      if (state.frame) window.cancelAnimationFrame(state.frame);
      state.image.style.transformOrigin = 'center center';
    }

    button.classList.remove('is-zoomed');
    this.zoomStates.delete(button);
  }

  openLightbox(index) {
    if (!this.lightboxEnabled || !this.lightbox || !this.lightboxImage) return;

    const imageIndex = this.findNearestImageIndex(index);
    if (imageIndex < 0) return;

    this.lightboxIndex = imageIndex;
    this.updateLightboxImage(false);
    this.lightbox.classList.add('is-open');
    this.lightbox.setAttribute('aria-hidden', 'false');
    document.body.classList.add('rrr-lightbox-open');

    window.setTimeout(() => this.lightboxClose?.focus(), 40);
  }

  closeLightbox() {
    if (!this.lightbox) return;

    this.lightbox.classList.remove('is-open');
    this.lightbox.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('rrr-lightbox-open');
    this.viewer?.focus({ preventScroll: true });
  }

  updateLightboxImage(animate = true) {
    const slide = this.slides[this.lightboxIndex];
    const sourceImage = slide?.querySelector('.rrr-product-gallery__main-image');
    const imageButton = slide?.querySelector('[data-rrr-image-button]');
    if (!sourceImage || !this.lightboxImage) return;

    const source =
      imageButton?.dataset.rrrFullSrc ||
      sourceImage.currentSrc ||
      sourceImage.src ||
      sourceImage.getAttribute('src');

    if (animate) this.lightboxImage.classList.add('is-changing');

    const applyImage = () => {
      this.lightboxImage.src = source;
      this.lightboxImage.alt = sourceImage.alt || '';
      this.lightboxImage.onload = () => this.lightboxImage.classList.remove('is-changing');
      this.lightboxImage.onerror = () => this.lightboxImage.classList.remove('is-changing');
    };

    if (animate) {
      window.setTimeout(applyImage, 120);
    } else {
      applyImage();
    }

    this.preloadLightboxNeighbours();
  }

  showPreviousLightboxImage() {
    const index = this.findImageIndex(this.lightboxIndex, -1);
    if (index < 0) return;
    this.lightboxIndex = index;
    this.updateLightboxImage();
  }

  showNextLightboxImage() {
    const index = this.findImageIndex(this.lightboxIndex, 1);
    if (index < 0) return;
    this.lightboxIndex = index;
    this.updateLightboxImage();
  }

  findNearestImageIndex(startIndex) {
    if (this.slides[startIndex]?.dataset.mediaType === 'image') return startIndex;
    return this.findImageIndex(startIndex, 1);
  }

  findImageIndex(startIndex, direction) {
    let index = startIndex;

    for (let step = 0; step < this.slides.length; step += 1) {
      index = (index + direction + this.slides.length) % this.slides.length;
      if (this.slides[index]?.dataset.mediaType === 'image') return index;
    }

    return -1;
  }

  preloadAround(index) {
    [index, this.getPreviousIndex(index), this.getNextIndex(index)].forEach((slideIndex) => {
      const slide = this.slides[slideIndex];
      const image = slide?.querySelector('.rrr-product-gallery__main-image');
      const button = slide?.querySelector('[data-rrr-image-button]');
      if (!image) return;

      image.loading = 'eager';
      this.preloadSource(button?.dataset.rrrFullSrc || image.currentSrc || image.src);
    });
  }

  preloadLightboxNeighbours() {
    const previous = this.findImageIndex(this.lightboxIndex, -1);
    const next = this.findImageIndex(this.lightboxIndex, 1);

    [previous, next].forEach((index) => {
      const slide = this.slides[index];
      const button = slide?.querySelector('[data-rrr-image-button]');
      const image = slide?.querySelector('.rrr-product-gallery__main-image');
      this.preloadSource(button?.dataset.rrrFullSrc || image?.currentSrc || image?.src);
    });
  }

  preloadSource(source) {
    if (!source || this.preloadedSources.has(source)) return;

    this.preloadedSources.add(source);
    const preloadImage = new Image();
    preloadImage.decoding = 'async';
    preloadImage.src = source;
  }

  pauseMedia(slide) {
    slide.querySelectorAll('video').forEach((video) => {
      try {
        video.pause();
      } catch (error) {
        console.warn('RRR gallery: video pause failed.', error);
      }
    });

    slide.querySelectorAll('model-viewer').forEach((model) => {
      if (typeof model.pause === 'function') model.pause();
    });
  }

  dispatchGalleryChange(source) {
    const slide = this.slides[this.currentIndex];
    this.gallery.dispatchEvent(
      new CustomEvent('rrr:gallery:change', {
        bubbles: true,
        detail: {
          sectionId: this.sectionId,
          productId: this.gallery.dataset.productId,
          mediaId: slide?.dataset.mediaId,
          mediaIndex: this.currentIndex,
          source
        }
      })
    );
  }

  getPreviousIndex(index) {
    return (index - 1 + this.slides.length) % this.slides.length;
  }

  getNextIndex(index) {
    return (index + 1) % this.slides.length;
  }

  clampIndex(index) {
    return Math.max(0, Math.min(Number(index) || 0, this.slides.length - 1));
  }
}

function initializeRRRProductGalleries(container = document) {
  container.querySelectorAll('[data-rrr-product-gallery]').forEach((gallery) => {
    if (gallery.dataset.rrrInitialized === 'true') return;
    gallery.dataset.rrrInitialized = 'true';
    new RRRProductGallery(gallery);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initializeRRRProductGalleries());
} else {
  initializeRRRProductGalleries();
}

document.addEventListener('shopify:section:load', (event) => {
  initializeRRRProductGalleries(event.target);
});
